import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ButtonGroup,
  Flex,
  HStack,
  IconButton,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  useStyles,
} from '@chakra-ui/react';
import KeyboardArrowLeftOutlinedIcon from '@gamma/icons/KeyboardArrowLeftOutlinedIcon';
import KeyboardArrowRightOutlinedIcon from '@gamma/icons/KeyboardArrowRightOutlinedIcon';
import { getClampedPageIndex } from '@investigator/features/entities/utils/helpers';
import { i18n } from '@investigator/shared/localization';

const { of, items, page } = i18n.pages.entities.notesPopover.pagination;

type NotesPopoverPageSize = 5 | 10 | 20 | 25 | 50 | 100;
const PAGE_SIZES: NotesPopoverPageSize[] = [5, 10, 20, 25, 50, 100];

interface NotesPopoverPaginationProps {
  pageSize: number;
  pageIndex: number;
  pageCount: number;
  itemCount: number;
  onPageSizeChange: (size: number) => void;
  onPageIndexChange: (index: number) => void;
}

const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
  const paste = e.clipboardData.getData('text');
  if (!/^\d+$/.test(paste)) {
    e.preventDefault();
  }
};

export const NotesPopoverPagination = ({
  pageSize,
  pageIndex,
  pageCount,
  itemCount,
  onPageSizeChange,
  onPageIndexChange,
}: NotesPopoverPaginationProps) => {
  const pageInputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<number>(pageIndex + 1);
  const styles = useStyles();

  const lastItemIndex = useMemo(() => {
    const index = (pageIndex + 1) * pageSize;
    return itemCount < index ? itemCount : index;
  }, [pageIndex, pageSize, itemCount]);

  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const value = Number((e.target as HTMLInputElement).value);
        onPageIndexChange(getClampedPageIndex(value, pageCount));
        // Don't blur - keeps input focused
        return;
      }

      // Handle Escape - blur the input so Escape can bubble to Popover
      if (e.key === 'Escape') {
        pageInputRef.current?.blur();
        return; // Let the event bubble
      }

      // Only allow numbers and navigation keys
      if (
        !/[0-9]/.test(e.key) &&
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(
          e.key,
        )
      ) {
        e.preventDefault();
      }
    },
    [pageCount, onPageIndexChange],
  );

  const handleBlur = useCallback(() => {
    onPageIndexChange(getClampedPageIndex(inputValue, pageCount));
  }, [inputValue, pageCount, onPageIndexChange]);

  const handlePageSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newSize = Number(e.target.value);
      onPageSizeChange(newSize);
    },
    [onPageSizeChange],
  );

  const handlePageInputChange = useCallback((value: string) => {
    const page = value ? Number(value) : 0;
    setInputValue(page);
  }, []);

  const handlePreviousPage = useCallback(() => {
    if (canPreviousPage) {
      onPageIndexChange(pageIndex - 1);
    }
  }, [canPreviousPage, pageIndex, onPageIndexChange]);

  const handleNextPage = useCallback(() => {
    if (canNextPage) {
      onPageIndexChange(pageIndex + 1);
    }
  }, [canNextPage, pageIndex, onPageIndexChange]);

  useEffect(() => {
    // If pageIndex changes, update inputValue
    let newValue = pageIndex + 1;

    // Clamp to pageCount if needed
    if (pageCount && newValue > pageCount) {
      newValue = pageCount;
    }

    setInputValue(newValue);
  }, [pageIndex, pageCount]);

  return (
    <Flex __css={styles.pagination} data-testid="notes-popover-pagination">
      <HStack alignItems="center">
        <Select
          variant="pagination"
          size="sm"
          width="auto"
          aria-label="Page size"
          value={pageSize}
          onChange={handlePageSizeChange}
          bg="layer.1"
          borderRadius="base"
          px={0}
          py={1.5}
          data-testid="notes-popover-pagination-size"
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Select>
        <Text>
          {pageIndex * pageSize + 1}-{lastItemIndex} {of} {itemCount} {items}
        </Text>
      </HStack>

      <Flex alignItems="center" justifyContent="space-between">
        <HStack alignItems="center">
          <label
            style={{ display: 'contents' }}
            htmlFor="notes-popover-pagination-input"
          >
            <Text textStyle="body-md">{page}</Text>
            <NumberInput
              id="notes-popover-pagination-input"
              value={inputValue}
              onChange={handlePageInputChange}
              onBlur={handleBlur}
              size="sm"
              min={1}
              max={pageCount || 1}
              bg="layer.1"
              borderRadius="base"
              overflow="hidden"
              minW={9}
              _focusWithin={{
                outlineWidth: '2px',
                outlineStyle: 'solid',
                outlineColor: 'state.selected',
              }}
            >
              <NumberInputField
                ref={pageInputRef}
                onKeyDown={handleKeyDown}
                onPaste={handleOnPaste}
                size={`${pageIndex + 1}`.length}
                width="full"
                minW="full"
                pl={1.5}
                pr={1}
                textAlign="center"
                bgColor="inherit"
                borderRadius="base"
                aria-label="page number"
              />
            </NumberInput>
          </label>
          <Text textStyle="body-md">
            {of} {pageCount}
          </Text>

          <ButtonGroup
            ml={2}
            spacing={0}
            variant="solid"
            colorScheme="gray"
            size="box-md"
          >
            <IconButton
              onClick={handlePreviousPage}
              opacity={canPreviousPage ? 1 : 0.4}
              cursor={canPreviousPage ? 'pointer' : 'not-allowed'}
              pointerEvents={canPreviousPage ? 'auto' : 'none'}
              icon={<KeyboardArrowLeftOutlinedIcon size="sm" />}
              aria-label="previous page"
              aria-disabled={!canPreviousPage}
              borderRightRadius={0}
              borderRightWidth="0.5px"
            />
            <IconButton
              onClick={handleNextPage}
              opacity={canNextPage ? 1 : 0.4}
              cursor={canNextPage ? 'pointer' : 'not-allowed'}
              pointerEvents={canNextPage ? 'auto' : 'none'}
              icon={<KeyboardArrowRightOutlinedIcon size="sm" />}
              aria-label="next page"
              aria-disabled={!canNextPage}
              borderLeftRadius={0}
              borderLeftWidth="0.5px"
            />
          </ButtonGroup>
        </HStack>
      </Flex>
    </Flex>
  );
};
